<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <!-- Instructions -->
  <div v-if="returnDetailStore.SSRE_RDV && response.returnStatusCode === 300" class="margin-top-m return-instructions hide-for-print">
    <ReturnInstructions
      :return-drop-off-method="response.returnDropOffMethod"
      :return-resolution="returnDetailStore.SSRE_RDV"
      :is-mkp-order="response.isMkpOrder"
      page="return-details"
      make-ups-clickable
      :is-replace-exchange="true"
      :drop-off-date="response.dropOffByDate"
      @ups-click="showLocatorSlideout"
    />
  </div>

  <!-- UPS locations slideout -->
  <!-- Order header -->
  <!-- eslint-disable max-len -->
  <div
    id="order-header"
    class="border-top text-right margin-top-m padding-left-s padding-right-s padding-top-xs padding-bottom-xs background-background-color hide-for-print"
  >
    <NuxtLink v-if="orderDetailsLink" id="order-number" :to="orderDetailsLink" class="link-body">
      Order # <span v-text="orderNumber"></span>
    </NuxtLink>
  </div>
  <!-- Items Return + Replacement/Exchange -->
  <div v-for="shipment in allShipments" :key="shipment.type" class="margin-top-m hide-for-print" :class="shipment.class">
    <!-- header -->
    <div>
      <h4 v-text="shipment.header.status"></h4>
      <!-- subheader -->
      <p class="margin-top-xxs" v-text="shipment.header.subStatus"></p>
    </div>
    <!-- progressbar -->
    <ProgressBar class="margin-top-m" :progress-bar-data="shipment.progressBar.steps" :progress-bar-value="shipment.progressBar.value" />
    <div v-for="item in shipment.items" :key="item.detail.productId" class="margin-top-m grid-x">
      <!-- product thumbnail-->
      <ProductCard class="cell small-12 large-auto" :product="item.detail" :image-width="108" click-action="linkToPdp" />
      <!-- ctas -->
      <div
        :id="`${shipment.type?.toLowerCase()}-${isLabelFreeSSRE ? 'qr' : 'ctas'}`"
        class="small-12 small-order-3 large-order-2 large-3 small-margin-top-m large-margin-top-z"
      >
        <QRCode
          v-if="isLabelFreeSSRE && shipment.type === 'RETURN' && response.returnStatusCode === 300"
          :qrcode-url="response.dropOffUrl"
          :scan-text="getScanText"
        />

        <template v-else>
          <component :is="ctaVNode" v-for="(ctaVNode, index) in item.ctas" :key="index" />
        </template>
      </div>
      <ReturnPolicyMessage class="cell margin-top-xs small-order-2 large-order-3" :product="item.detail" />
    </div>
  </div>
  <!-- Summary -->
  <ReturnSummary
    v-if="response.returnDropOffMethod"
    class="padding-top-m border-top hide-for-print"
    hide-order-number
    :return-details="response"
    :replace-and-exchange-option="returnDetailStore.SSRE_RDV"
    page="return-details"
  />

  <PrintReturnLabel
    v-if="showPrint"
    class="show-for-print"
    page="return-details"
    :ssre-type="returnDetailStore.SSRE_RDV"
    :return-details="enhancedReturnDetails"
  />
</template>

<script setup>
//imports
import { ref, computed, h, markRaw, defineAsyncComponent, onMounted } from 'vue';
import { fireTag } from '@vue-util/tagManager';

//local components
import ProgressBar from '@/components/ProgressBar';
import ReturnSummary from '@/components/returns/ReturnSummary';
import ProductCard from '@/components/ProductCard';
import ReturnInstructions from '@/components/returns/ReturnInstructions';
import PrintReturnLabel from '@/components/returns/PrintReturnLabel';
import ReturnPolicyMessage from '@/components/returns/ReturnPolicyMessage';
import QRCode from '@/components/common/QRCode';

// Utility functions
import { transformProduct } from '@/utils/returns/TransformProducts';
import { getShipmentTrackingLabel, getShipmentStatus } from '@/utils/ShipmentStatus';
import { formatDate } from '@/utils/General';

//config & constant
import { RETURN_RESOLUTIONS, LOCATOR_TITLE, SCAN_QR_TEXT, RETURN_METHOD_KEY_MAP } from 'config';
import { LOCATOR_LOCATION_RADIUS, LOCATOR_RESULT_LIMIT } from '#shared/constants';

//Router, Pinia Stores, Composables
const router = useRouter();
const route = useRoute();
const returnDetailStore = useReturnDetailStore();
const returnItemStore = useReturnItemStore();
const trackPackageStore = useTrackPackageStore();
const envPropsStore = useEnvPropsStore();
const { registerSlideout, toggleSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();

//Local Variable
const showPrint = ref(false);

//computed property
const response = computed(() => returnDetailStore.returnDetails);

const enhancedReturnDetails = computed(() => {
  const shippingLabels = returnItemStore.shippingDetails.shippingLabels ?? [];
  const shippingLabel = shippingLabels[0] ?? {};

  return {
    ...response.value,
    ...(response.value.pidToken || response.value.isRmaReturn
      ? {
          labelImageUrl: shippingLabel?.base64Label,
          rmaId: response.value.pendingReturnId,
        }
      : {}),
  };
});

//Order header
const orderNumber = computed(() => response.value.orderNumber);

const orderDetailsLink = computed(() => {
  if (!response.value.orderNumber || !response.value.orderToken) return '';
  return {
    name: 'Order Details',
    params: { orderNumber: response.value.orderNumber },
    query: { o: response.value.orderToken },
  };
});

const isLabelFreeSSRE = computed(() => returnDetailStore.SSRE_RDV && response.value.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree);

const getScanText = computed(() => SCAN_QR_TEXT.config()[response.value.returnDropOffMethod]);
//prepare item functions
const showTrackingSlideout = async ({ type, trackingNumber, carrierName }) => {
  const isReplaceOrExchange = [RETURN_RESOLUTIONS.REPLACEMENT, RETURN_RESOLUTIONS.EXCHANGE].includes(type);

  const payload = {
    trackingNumber,
    carrierName,
    isMkpShipment: response.value.isMkpOrder,
    ...(isReplaceOrExchange && {
      isLtlShipment: false,
      shipmentType: 'STH',
    }),
  };
  trackPackageStore.GET_TRACKING_DETAILS(payload);
  const slideoutId = `track-details-slideout-${type.toLowerCase()}`;
  registerSlideout({
    id: slideoutId,
    title: 'Tracking Details',
    component: markRaw(
      defineAsyncComponent(() => import(/* webpackChunkName: "tracking-details-slideout" */ '@/components/ppe/TrackingDetailsSlideout.vue')),
    ),
  });
  toggleSlideout(slideoutId);
};
async function showLocatorSlideout(event) {
  const contactAddress = response.value.contact?.address || {};
  const address = [
    contactAddress.addressLine1,
    contactAddress.addressLine2,
    contactAddress.addressLine3,
    contactAddress.city,
    contactAddress.state,
    contactAddress.postalCode,
  ].reduce((acc, field) => {
    if (!field) return acc;
    if (acc) return `${acc}, ${field}`;
    return field;
  }, '');
  // TBD: setup analytics
  registerSlideout({
    id: 'return-details-locator-slideout',
    title: LOCATOR_TITLE[response.value.returnDropOffMethod],
    elementIdToFocus: event?.target.id,
    props: {
      typeOf: response.value.returnDropOffMethod,
      address,
      newGoogleApiCall: response.value.isNewGoogleAPICallEnabled,
      useMspLocatorApi: true,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });
  toggleSlideout('return-details-locator-slideout');
}
const showReturnLabel = async (event) => {
  showPrint.value = true;
  event.preventDefault();
  fireTag('link', {
    event_name: 'return details link',
    link_name: 'ups get return label',
  });
  registerModal({
    id: 'return-label',
    title: 'How to get your return label',
    width: '628px',
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/GetReturnLabel'))),
  });
  toggleModal('return-label');
};
const returnItemAction = (item) =>
  router.resolve({
    name: 'Return Items',
    query: {
      o: response.value.orderToken,
      upcnumber: item.itemDetails.upcNumber,
      lid: 'return_items',
    },
  }).href;
const createCta = (cta) => h(cta.tag, { class: `button expanded ${cta.class}`, ...cta.props }, cta.label);
const getItemCtas = (code, type, item) => {
  if ([300].includes(code)) {
    return [
      {
        tag: 'button',
        class: 'primary',
        label: 'Return Label',
        props: {
          type: 'button',
          onClick: showReturnLabel,
        },
      },
    ].map(createCta);
  }

  if ([301, 302, 303].includes(code)) {
    return [
      ...(item.trackingNumber
        ? [
            {
              tag: 'button',
              class: 'secondary',
              label: 'Track return',
              props: {
                type: 'button',
                onClick: () => showTrackingSlideout({ type, trackingNumber: item.trackingNumber, carrierName: item.carrierName }),
              },
            },
          ]
        : []),
    ].map(createCta);
  }

  if ([500, 504, 700, 704].includes(code)) {
    return [
      ...(item.itemDetails.buyAgainFlag
        ? [
            {
              tag: 'a',
              class: 'secondary',
              label: 'Buy Again',
              props: {
                href: `/shop/product/?ID=${item.itemDetails.productId}`,
              },
            },
          ]
        : []),
    ].map(createCta);
  }

  if ([501, 502, 503, 701, 702, 703].includes(code)) {
    return [
      ...(item.trackingNumber
        ? [
            {
              tag: 'button',
              class: 'primary',
              label: `Track ${type}`,
              props: {
                type: 'button',
                onClick: () => showTrackingSlideout({ type, trackingNumber: item.trackingNumber, carrierName: item.carrierName }),
              },
            },
          ]
        : []),
      ...(item.itemDetails.returnEligibleFlag
        ? [
            {
              tag: 'a',
              class: 'secondary margin-top-xs',
              label: 'Return Item',
              props: {
                href: returnItemAction(item),
              },
            },
          ]
        : []),
    ].map(createCta);
  }

  return [];
};

/**
 * Return + Replacement/Exchange Items
 */
const allShipments = computed(() => {
  const {
    items = [],
    substituteShipments = [],
    returnStatusCode,
    returnStatusDescription,
    returnDropOffMethod,
    returnSubmittedDate,
    returnTrackingNumber,
  } = response.value;

  let returnSubStatus = `Submitted on ${formatDate(returnSubmittedDate)}`;
  if (returnStatusCode === 303 && response.value.refundIssuedDate) {
    returnSubStatus = `Received on ${formatDate(response.value.refundIssuedDate)}`;
  }
  const mergedShipments = [
    {
      type: 'RETURN', // RETURN Shipment,
      shipmentTracking: 'UPS_RETURN',
      trackingNumber: returnTrackingNumber,
      carrierName: returnDropOffMethod,
      status: returnStatusDescription,
      subStatus: returnSubStatus,
      statusCode: returnStatusCode,
      shipmentItems: items,
    },
    ...substituteShipments.map((shipment) => ({
      type: shipment.type,
      shipmentTracking: shipment.type,
      status: shipment.shipmentStatus,
      trackingNumber: shipment.trackingNos,
      carrierName: shipment.carrierName,
      subStatus: shipment.shipmentSubStatus,
      statusCode: shipment.shipmentStatusCode,
      shipmentItems: shipment.substituteItems,
    })),
  ];

  return mergedShipments.map((shipment, index) => {
    const { status, subStatus, statusCode, shipmentTracking, type, shipmentItems, trackingNumber, carrierName } = shipment;
    let shipmentTrackingSteps = getShipmentTrackingLabel(shipmentTracking);
    let currentTrackingStep = getShipmentStatus(statusCode).statusBarValue || 0;
    if (envPropsStore.envProps.isMew) {
      if ([301, 302, 502, 702].includes(statusCode)) currentTrackingStep = 50;
      if ([501, 701].includes(statusCode)) currentTrackingStep = 1;
      const codes = [302, 501, 701];
      shipmentTrackingSteps = shipmentTrackingSteps.filter((s) => !codes.includes(s.shipmentStatusCode));
    }

    return {
      header: {
        status,
        subStatus,
      },
      progressBar: {
        steps: shipmentTrackingSteps,
        value: currentTrackingStep,
      },
      items: shipmentItems.map((item) => ({
        detail: transformProduct(item, response.value.isMkpOrder, 'return', true),
        ctas: getItemCtas(statusCode, type, { ...item, trackingNumber, carrierName }),
      })),
      type,
      class: index > 0 ? 'border-top padding-top-m' : '',
    };
  });
});

onMounted(() => {
  if (route.query.showLocatorModal === 'true' && response.value?.returnStatusCode === 300) showLocatorSlideout();
});
</script>
<style lang="scss" scoped>
#order-header {
  background-color: $background-color;
}
</style>
